import { createFileRoute } from "@tanstack/react-router";
import { Mail, MessageSquare, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";

import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { client } from "@/utils/orpc";
import { Button } from "@super-resume-new/ui/components/button";
import { Input } from "@super-resume-new/ui/components/input";
import { Textarea } from "@super-resume-new/ui/components/textarea";
import { Label } from "@super-resume-new/ui/components/label";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/contact")({
  head: () =>
    seo({
      title: "Contact JobCues — Support & Feedback",
      description:
        "Get in touch with the JobCues team about billing, a job listing correction, an API question, or product feedback. We read every message.",
      path: "/contact",
    }),
  component: ContactPageComponent,
});

function ContactPageComponent() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: () => client.contact.submit({ name, email, subject, message }),
    onSuccess: () => {
      setSent(true);
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    },
    onError: () => {
      toast.error("Something went wrong. Try emailing us directly.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate();
  };

  return (
    <PageLayout id="contact-page-content" className="max-w-7xl">
      <PageHeader
        title="Contact us"
        description="We read every message and usually reply within a day or two."
        icon={MessageSquare}
      />

      <div className="grid gap-8 lg:grid-cols-5">
        {/* Form */}
        <div className="lg:col-span-3">
          <div className="rounded-xl border border-border bg-card shadow-sm">
            <div className="border-b border-border px-6 py-4">
              <h2 className="text-sm font-semibold text-foreground">Send a message</h2>
            </div>

            {sent ? (
              <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
                <div className="flex size-10 items-center justify-center rounded-full border border-border bg-muted">
                  <Send className="size-4 text-violet-500" aria-hidden="true" />
                </div>
                <p className="text-sm font-semibold text-foreground">Message sent</p>
                <p className="max-w-xs text-[13px] leading-relaxed text-muted-foreground">
                  Thanks for reaching out. We'll get back to you at the email you provided.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-2 text-[13px] text-violet-500 underline underline-offset-4 hover:text-violet-600"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-6">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="contact-name" className="text-[13px] font-medium text-foreground">
                      Name
                    </Label>
                    <Input
                      id="contact-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      required
                      disabled={isPending}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="contact-email" className="text-[13px] font-medium text-foreground">
                      Email
                    </Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      disabled={isPending}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="contact-subject" className="text-[13px] font-medium text-foreground">
                    Subject
                  </Label>
                  <Input
                    id="contact-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="What's this about?"
                    required
                    disabled={isPending}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="contact-message" className="text-[13px] font-medium text-foreground">
                    Message
                  </Label>
                  <Textarea
                    id="contact-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what's on your mind..."
                    rows={6}
                    required
                    disabled={isPending}
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isPending || !name || !email || !subject || !message}
                  className="self-start"
                >
                  {isPending ? "Sending..." : "Send message"}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-4 lg:col-span-2">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2.5">
              <div className="flex size-8 items-center justify-center rounded-lg border border-border bg-muted">
                <Mail className="size-4 text-violet-500" aria-hidden="true" />
              </div>
              <h2 className="text-sm font-semibold text-foreground">Email us directly</h2>
            </div>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              Prefer email? Write to us at:
            </p>
            <a
              href="mailto:contact@jobcues.com"
              className="mt-2 block text-[13px] font-medium text-violet-500 underline underline-offset-4 hover:text-violet-600"
            >
              contact@jobcues.com
            </a>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <h2 className="mb-2 text-sm font-semibold text-foreground">What to include</h2>
            <ul className="space-y-2 text-[13px] leading-relaxed text-muted-foreground">
              <li>Your account email, if your question is account-related</li>
              <li>Steps to reproduce, if you're reporting a bug</li>
              <li>Any relevant screenshots or error text</li>
            </ul>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
